/**
 * Formatters Utility
 *
 * Centralized formatting utilities for currency, dates, and percentages.
 * Eliminates DRY violations across frontend pages.
 *
 * @example
 * import { Formatters } from '../index';
 *
 * Formatters.currency(1500);        // "$1,500"
 * Formatters.date(new Date());      // "Dec 19, 2025"
 * Formatters.dateLong(new Date());  // "December 19, 2025"
 * Formatters.percent(15.5);         // "+15.5%"
 */

/**
 * Supported currencies for formatting
 */
export type Currency = 'USD' | 'TRY' | 'EUR' | 'GBP';

/**
 * Supported locales for formatting
 */
export type Locale = 'en-US' | 'tr-TR' | 'de-DE';

/**
 * Date format options
 */
export type DateFormat = 'short' | 'medium' | 'long';

/**
 * Format a number as currency
 *
 * @param value - The numeric value to format
 * @param currency - The currency code (default: 'USD')
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1500);              // "$1,500"
 * formatCurrency(1500, 'TRY');       // "₺1,500"
 * formatCurrency(1500.50, 'USD', 2); // "$1,500.50"
 */
export function formatCurrency(
  value: number,
  currency: Currency = 'USD',
  locale: Locale = 'en-US',
  decimals: number = 0
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a date with short month (e.g., "Dec 19, 2025")
 *
 * @param date - The date to format (Date, string, or null)
 * @param locale - The locale for formatting (default: 'en-US')
 * @param fallback - Value to return if date is null/undefined (default: '-')
 * @returns Formatted date string
 *
 * @example
 * formatDateShort(new Date());           // "Dec 19, 2025"
 * formatDateShort('2025-12-19');         // "Dec 19, 2025"
 * formatDateShort(null);                 // "-"
 * formatDateShort(null, 'en-US', 'N/A'); // "N/A"
 */
export function formatDateShort(
  date: Date | string | null | undefined,
  locale: Locale = 'en-US',
  fallback: string = '-'
): string {
  if (!date) return fallback;
  return new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date with long month (e.g., "December 19, 2025")
 *
 * @param date - The date to format (Date, string, or null)
 * @param locale - The locale for formatting (default: 'en-US')
 * @param fallback - Value to return if date is null/undefined (default: 'N/A')
 * @returns Formatted date string
 *
 * @example
 * formatDateLong(new Date());       // "December 19, 2025"
 * formatDateLong('2025-12-19');     // "December 19, 2025"
 * formatDateLong(null);             // "N/A"
 */
export function formatDateLong(
  date: Date | string | null | undefined,
  locale: Locale = 'en-US',
  fallback: string = 'N/A'
): string {
  if (!date) return fallback;
  return new Date(date).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date with configurable format
 *
 * @param date - The date to format
 * @param format - The format to use ('short', 'medium', 'long')
 * @param locale - The locale for formatting
 * @param fallback - Value to return if date is null/undefined
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | null | undefined,
  format: DateFormat = 'short',
  locale: Locale = 'en-US',
  fallback: string = '-'
): string {
  if (!date) return fallback;

  const monthFormats: Record<DateFormat, 'short' | 'long' | 'numeric'> = {
    short: 'short',
    medium: 'short',
    long: 'long',
  };

  return new Date(date).toLocaleDateString(locale, {
    month: monthFormats[format],
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a percentage with sign (e.g., "+15.5%" or "-5.2%")
 *
 * @param value - The percentage value
 * @param decimals - Number of decimal places (default: 1)
 * @param showSign - Whether to show + for positive values (default: true)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(15.5);        // "+15.5%"
 * formatPercent(-5.2);        // "-5.2%"
 * formatPercent(0);           // "0.0%"
 * formatPercent(15.5, 0);     // "+16%"
 * formatPercent(15.5, 1, false); // "15.5%"
 */
export function formatPercent(
  value: number,
  decimals: number = 1,
  showSign: boolean = true
): string {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Format a number with thousand separators
 *
 * @param value - The numeric value to format
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1500000);           // "1,500,000"
 * formatNumber(1500000, 'tr-TR');  // "1.500.000"
 */
export function formatNumber(value: number, locale: Locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Unified Formatters object for convenience
 *
 * @example
 * import { Formatters } from '../index';
 *
 * Formatters.currency(1500);
 * Formatters.date(new Date());
 * Formatters.dateLong(new Date());
 * Formatters.dateShort(new Date());
 * Formatters.percent(15.5);
 * Formatters.number(1500000);
 */
export const Formatters = {
  currency: formatCurrency,
  date: formatDate,
  dateLong: formatDateLong,
  dateShort: formatDateShort,
  percent: formatPercent,
  number: formatNumber,
} as const;
